import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { scopedCollection } from '@adornis/buchungsheld-db/server/scoped-collection.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { buildEmail } from '@adornis/mails/db/templates.js';
import type { TemplateResult } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import sanitizeHtml from 'sanitize-html';
import { MatchingWeekSettings } from '../db/settings.js';
import { escapeHtmlAttribute } from '../shared/escape-html.js';
import { personalizeFreeFormText } from '../shared/free-form-mail.js';

export const toMailSafeHtml = (editorHtml: string): string => {
  const sanitized = sanitizeHtml(editorHtml, {
    allowedTags: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'del',
      'code',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'a',
      'span',
      'blockquote',
    ],
    allowedAttributes: { '*': ['style'], ol: ['start', 'style'], a: ['href', 'target', 'rel'] },
    allowedSchemes: ['http', 'https', 'mailto'],
    allowProtocolRelative: false,
    nonTextTags: ['script', 'style', 'textarea', 'option', 'iframe'],
    transformTags: {
      '*': (tagName: string, attribs: Record<string, string>) => {
        if (tagName === 'div' && attribs.class?.split(/\s+/).includes('list-marker')) {
          // A disallowed non-text tag removes the entire marker subtree.
          return { tagName: 'script', attribs: {} };
        }
        if (/^(tr|x)-text$/.test(tagName)) tagName = 'p';
        else if (/^(tr|x)-h[1-6]$/.test(tagName)) tagName = tagName.replace(/^(tr|x)-/, '');
        else if (/^(tr|x)-link$/.test(tagName)) tagName = 'a';
        if (tagName === 'a') {
          return { tagName, attribs: { href: attribs.href ?? '', target: '_blank', rel: 'noopener noreferrer' } };
        }
        return { tagName, attribs };
      },
    },
    allowedStyles: {
      '*': {
        color: [/^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|[a-z]+)$/i],
        'background-color': [/^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|[a-z]+)$/i],
        'text-align': [/^(left|right|center|justify)$/],
        'font-size': [/^\d+(\.\d+)?(px|em|rem|%)$/],
      },
    },
  });
  return sanitized.replace(/<p([^>]*)>([\s\S]*?)<\/p>/g, (paragraph: string, attrs: string, contents: string) => {
    const text = sanitizeHtml(contents, { allowedTags: [], allowedAttributes: {} });
    return text.replace(/&nbsp;|&#160;|&#x0*a0;/gi, '').trim() ? paragraph : `<p${attrs}>&nbsp;</p>`;
  });
};

export const composeFreeFormMail = (args: {
  subject: string;
  bodyHtml: string;
  recipient: { firstName?: Maybe<string>; lastName?: Maybe<string>; email?: Maybe<string> };
  logoConfig?: { src: string; alt: string; width?: number; padding?: number };
}): { subject: string; html: TemplateResult } => {
  const subject = personalizeFreeFormText(args.subject, args.recipient, value => value);
  const body = toMailSafeHtml(personalizeFreeFormText(args.bodyHtml, args.recipient, escapeHtmlAttribute));
  return {
    subject,
    html: buildEmail({
      title: escapeHtmlAttribute(subject),
      contents: unsafeHTML(body),
      footer: '',
      logoConfig: args.logoConfig,
    }),
  };
};

export const loadFreeFormMailLogoConfig = async () => {
  const settingsCol = await scopedCollection(MatchingWeekSettings);
  const settings = await settingsCol.findOne({});
  if (!settings?.emailBannerFileId) return undefined;
  return {
    src: A.absoluteUrl(ImageFile.getServeLink(settings.emailBannerFileId)),
    alt: 'Matching Week',
    width: 600,
    padding: 0,
  };
};
